import React, { FC, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apollo } from "../../apollo";
import { UserStatus } from "../../generated/ups/graphql";
import { useTheme } from "@carrier-io/air-react/styles";
import Box from "@carrier-io/air-react/Box";
import { Skeleton, Typography } from "@carrier-io/air-react";
import { Button } from "../button/Button";
import { ConfirmDeactivationPopup } from "./ConfirmDeactivationPopup";
import Notification from "../../components/notification/Notification";
import { displayError } from "../../constants/error";
import { NotificationBgColor } from "../../types/notification";
import { UIUserStatus, UserSearchParam } from "../../types/user";
import { useUpdateUser } from "../../hooks/useUpdateUser";

type DeactivateUserProps = {
  isLoading: boolean;
  existingUserStatus?: string;
  refetchExistingUser?: () => void;
};

export const DeactivateUser: FC<DeactivateUserProps> = ({
  isLoading,
  existingUserStatus,
  refetchExistingUser,
}) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [confirmPopupOpened, setConfirmPopupOpened] = useState(false);
  const [isNotificationOpened, setIsNotificationOpened] = useState(false);
  const [notificationText, setNotificationText] = useState("");
  const [notificationBgColor, setNotificationBgColor] = useState(
    NotificationBgColor.INFO,
  );
  const [updateUser, { loading: loadingUpdate }] = useUpdateUser();

  const disableDeactivation = [
    UIUserStatus.PENDING,
    UIUserStatus.INACTIVE,
  ].includes(existingUserStatus as UIUserStatus);
  const username = searchParams.get(UserSearchParam.USERNAME);
  const region = searchParams.get(UserSearchParam.REGION);

  const deactivateUser = () => {
    setConfirmPopupOpened(true);
  };

  const handleCancel = () => {
    setConfirmPopupOpened(false);
  };

  const handleConfirm = async () => {
    if (!region || !username || disableDeactivation) {
      setNotificationBgColor(NotificationBgColor.WARNING);
      setNotificationText("Invalid data");
      setIsNotificationOpened(true);

      return;
    }

    const variables = {
      region,
      username,
      input: {
        status: UserStatus.DISABLED,
      },
    };

    try {
      await updateUser({ variables });

      refetchExistingUser?.();
      setNotificationBgColor(NotificationBgColor.INFO);
      setNotificationText("User deactivated successfully");
      setIsNotificationOpened(true);
    } catch (error) {
      setNotificationBgColor(NotificationBgColor.ERROR);
      setNotificationText(
        displayError((error as apollo.ApolloError).graphQLErrors[0]),
      );
      setIsNotificationOpened(true);
    } finally {
      setConfirmPopupOpened(false);
    }
  };

  return (
    <Box
      data-testid="deactivate-user-section"
      sx={{
        paddingBottom: 3,
        marginBottom: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {isLoading ? (
        <Skeleton
          data-testid="status-loading-skeleton"
          height={127}
          width={300}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Typography variant="h6">Deactivation</Typography>
          <Button
            data-testid="deactivate-user-button"
            onClick={deactivateUser}
            isLoading={isLoading || loadingUpdate}
            disabled={isLoading || disableDeactivation}
            color="secondary"
            variant="outlined"
            sx={{
              fontWeight: "700 !important",
              padding: "4px 12px",
              height: "32px !important",
            }}
          >
            Deactivate User
          </Button>
        </Box>
      )}
      <ConfirmDeactivationPopup
        opened={confirmPopupOpened}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
      <Notification
        isNotificationOpened={isNotificationOpened}
        textContent={notificationText}
        onCloseNotification={() => setIsNotificationOpened(false)}
        notificationBgColor={notificationBgColor}
      />
    </Box>
  );
};
